import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  CheckCircle2,
  Info,
  KeyRound,
  Loader2,
  MessageSquare,
  Plus,
  Save,
  Shield,
  Smartphone,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

const normalizeOriginatorList = (list, fallback = "") => {
  const items = (Array.isArray(list) ? list : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean);
  if (items.length > 0) return items;
  if (fallback.trim()) return [fallback.trim()];
  return [""];
};

const OtpApiSettings = ({ apiBase, isDark, textClass, labelClass }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ sms: {}, whatsapp: {} });
  const [form, setForm] = useState({
    sms_service_enabled: false,
    sms_api_url: "",
    sms_username: "",
    sms_password: "",
    sms_originators: [""],
    sms_default_originator: "",
    whatsapp_service_enabled: false,
    whatsapp_api_url: "",
    whatsapp_api_token: "",
    whatsapp_slot: 1,
    login_link_enabled: false,
    login_link_base_url: "https://cc2.etelekom.net/otp",
    login_link_secret: "",
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [tokenSet, setTokenSet] = useState(false);
  const [linkSecretSet, setLinkSecretSet] = useState(false);
  const [loginOtpMandatory, setLoginOtpMandatory] = useState(false);

  const inputClass = isDark
    ? "bg-slate-950/80 border-slate-700 text-white placeholder:text-slate-500"
    : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400";
  const panelClass = isDark
    ? "border-slate-800 bg-slate-900/60"
    : "border-gray-200 bg-white";
  const fieldWrapClass = isDark ? "bg-slate-950/40 border-slate-800" : "bg-gray-50/80 border-gray-100";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiBase}/otp-settings`);
        if (cancelled) return;
        const s = res.data.settings || {};
        const originators = normalizeOriginatorList(s.sms_originators, s.sms_originator || "");
        const defaultOriginator = s.sms_default_originator || originators.find(Boolean) || "";
        setForm({
          sms_service_enabled: !!s.sms_service_enabled,
          sms_api_url: s.sms_api_url || "",
          sms_username: s.sms_username || "",
          sms_password: "",
          sms_originators: originators,
          sms_default_originator: defaultOriginator,
          whatsapp_service_enabled: !!s.whatsapp_service_enabled,
          whatsapp_api_url: s.whatsapp_api_url || "",
          whatsapp_api_token: "",
          whatsapp_slot: s.whatsapp_slot || 1,
          login_link_enabled: !!s.login_link_enabled,
          login_link_base_url: s.login_link_base_url || "https://cc2.etelekom.net/otp",
          login_link_secret: "",
        });
        setPasswordSet(!!s.sms_password_set);
        setTokenSet(!!s.whatsapp_api_token_set);
        setLinkSecretSet(!!s.login_link_secret_set);
        setLoginOtpMandatory(!!s.login_otp_mandatory);
        setStatus(res.data.status || {});
      } catch (err) {
        toast.error(err.response?.data?.detail || "OTP ayarları yüklenemedi");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanedOriginators = form.sms_originators
        .map((item) => String(item || "").trim())
        .filter(Boolean);
      const defaultOriginator = cleanedOriginators.includes(form.sms_default_originator)
        ? form.sms_default_originator
        : (cleanedOriginators[0] || "");
      const payload = {
        ...form,
        login_otp_mandatory: loginOtpMandatory,
        sms_originators: cleanedOriginators,
        sms_default_originator: defaultOriginator,
      };
      const res = await axios.put(`${apiBase}/otp-settings`, payload);
      setStatus(res.data.status || {});
      const s = res.data.settings || {};
      setPasswordSet(!!s.sms_password_set);
      setTokenSet(!!s.whatsapp_api_token_set);
      setLinkSecretSet(!!s.login_link_secret_set);
      setLoginOtpMandatory(!!s.login_otp_mandatory);
      const savedOriginators = normalizeOriginatorList(s.sms_originators, s.sms_originator || "");
      setForm((prev) => ({
        ...prev,
        sms_password: "",
        whatsapp_api_token: "",
        login_link_secret: "",
        sms_originators: savedOriginators,
        sms_default_originator: s.sms_default_originator || savedOriginators.find(Boolean) || "",
      }));
      toast.success("OTP API ayarları kaydedildi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const updateOriginator = (index, value) => {
    setForm((prev) => {
      const next = [...prev.sms_originators];
      next[index] = value;
      const trimmed = value.trim();
      let defaultOriginator = prev.sms_default_originator;
      if (trimmed && (!defaultOriginator || !next.some((item) => item.trim() === defaultOriginator))) {
        defaultOriginator = trimmed;
      }
      return { ...prev, sms_originators: next, sms_default_originator: defaultOriginator };
    });
  };

  const addOriginator = () => {
    setForm((prev) => ({ ...prev, sms_originators: [...prev.sms_originators, ""] }));
  };

  const removeOriginator = (index) => {
    setForm((prev) => {
      const removed = prev.sms_originators[index]?.trim() || "";
      const next = prev.sms_originators.filter((_, i) => i !== index);
      const list = next.length > 0 ? next : [""];
      let defaultOriginator = prev.sms_default_originator;
      if (removed && defaultOriginator === removed) {
        defaultOriginator = list.find((item) => String(item).trim())?.trim() || "";
      }
      return { ...prev, sms_originators: list, sms_default_originator: defaultOriginator };
    });
  };

  const setDefaultOriginator = (value) => {
    setForm((prev) => ({ ...prev, sms_default_originator: value }));
  };

  const statusMeta = (item) => {
    if (item?.ready) {
      return {
        label: "Hazır",
        icon: CheckCircle2,
        badge: isDark ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-emerald-50 text-emerald-700 border-emerald-200",
        dot: "bg-emerald-500",
      };
    }
    if (item?.enabled) {
      return {
        label: "Eksik ayar",
        icon: XCircle,
        badge: isDark ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-amber-50 text-amber-700 border-amber-200",
        dot: "bg-amber-500",
      };
    }
    return {
      label: "Kapalı",
      icon: XCircle,
      badge: isDark ? "bg-slate-800 text-slate-400 border-slate-700" : "bg-gray-100 text-gray-500 border-gray-200",
      dot: "bg-slate-400",
    };
  };

  const smsStatus = statusMeta(status.sms);
  const waStatus = statusMeta(status.whatsapp);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <p className={`text-sm ${labelClass}`}>OTP ayarları yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      {/* Üst banner */}
      <div className={`relative overflow-hidden rounded-2xl border p-5 sm:p-6 ${isDark ? "border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950/40" : "border-blue-100 bg-gradient-to-br from-white via-blue-50/50 to-white"}`}>
        <div className={`absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${isDark ? "bg-blue-600/20" : "bg-blue-400/15"}`} />
        <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2.5 rounded-xl ${isDark ? "bg-blue-500/15 text-blue-400" : "bg-blue-100 text-blue-600"}`}>
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h2 className={`text-lg sm:text-xl font-semibold tracking-tight ${textClass}`}>OTP / 2FA API Ayarları</h2>
                <p className={`text-xs sm:text-sm mt-0.5 ${labelClass}`}>Giriş doğrulama kanalları — SMS ve WhatsApp servis yapılandırması</p>
              </div>
            </div>
            <div className={`mt-4 flex items-start gap-2 rounded-xl border px-3 py-2.5 text-xs sm:text-sm ${isDark ? "border-slate-700/80 bg-slate-950/40 text-slate-300" : "border-blue-100 bg-blue-50/60 text-blue-900/80"}`}>
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-blue-500" />
              <span>Kullanıcı bazında OTP aç/kapa <strong className={textClass}>Kullanıcılar</strong> ekranından yapılır. Authenticator (TOTP) her kullanıcı kendi panelinden etkinleştirir.</span>
            </div>
            <div className={`mt-3 rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${isDark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50/80"}`}>
              <div className="min-w-0">
                <p className={`text-sm font-semibold flex items-center gap-2 ${isDark ? "text-amber-200" : "text-amber-900"}`}>
                  <Shield className="w-4 h-4" /> Tüm kullanıcılar için 2FA zorunlu
                </p>
                <p className={`text-xs mt-1 ${labelClass}`}>
                  Aktif edildiğinde herkes girişte SMS/WhatsApp/Authenticator doğrulaması yapmak zorundadır.
                  GSM numarası olmayan kullanıcılar giriş yapamaz ve yöneticiye yönlendirilir.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Label htmlFor="mandatory-2fa" className={`text-xs ${labelClass}`}>{loginOtpMandatory ? "Açık" : "Kapalı"}</Label>
                <Switch id="mandatory-2fa" checked={loginOtpMandatory} onCheckedChange={setLoginOtpMandatory} />
              </div>
            </div>
          </div>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Kaydet
          </Button>
        </div>
      </div>

      {/* Durum kartları */}
      <div className="grid sm:grid-cols-2 gap-3">
        {[
          { key: "sms", title: "SMS OTP", sub: "Klasik SMS doğrulama", icon: Smartphone, meta: smsStatus, accent: "blue" },
          { key: "wa", title: "WhatsApp OTP", sub: "Anlık mesaj doğrulama", icon: MessageSquare, meta: waStatus, accent: "emerald" },
        ].map(({ key, title, sub, icon: Icon, meta, accent }) => (
          <div
            key={key}
            className={`rounded-xl border p-4 flex items-center gap-4 ${panelClass}`}
          >
            <div className={`p-3 rounded-xl shrink-0 ${accent === "blue"
              ? (isDark ? "bg-blue-500/15 text-blue-400" : "bg-blue-50 text-blue-600")
              : (isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-600")
            }`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className={`font-semibold text-sm ${textClass}`}>{title}</p>
              <p className={`text-xs ${labelClass}`}>{sub}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
              <Badge className={`text-[11px] ${meta.badge}`}>{meta.label}</Badge>
            </div>
          </div>
        ))}
        <div className={`sm:col-span-2 rounded-xl border p-4 flex items-center gap-4 ${panelClass}`}>
          <div className={`p-3 rounded-xl shrink-0 ${isDark ? "bg-violet-500/15 text-violet-400" : "bg-violet-50 text-violet-600"}`}>
            <KeyRound className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className={`font-semibold text-sm ${textClass}`}>Authenticator (TOTP)</p>
            <p className={`text-xs ${labelClass}`}>Google / Microsoft Authenticator — kullanıcı panelinden kurulur</p>
          </div>
          <Badge className={isDark ? "bg-violet-500/15 text-violet-400 border-violet-500/30" : "bg-violet-50 text-violet-700 border-violet-200"}>
            Kullanıcı ayarı
          </Badge>
        </div>
      </div>

      <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-5 relative">
        <input type="text" name="fake-otp-user" autoComplete="username" tabIndex={-1} aria-hidden="true" className="absolute opacity-0 pointer-events-none h-0 w-0" defaultValue="" readOnly />
        <input type="password" name="fake-otp-pass" autoComplete="current-password" tabIndex={-1} aria-hidden="true" className="absolute opacity-0 pointer-events-none h-0 w-0" defaultValue="" readOnly />

        <div className="grid lg:grid-cols-2 gap-5">
          {/* SMS panel */}
          <section className={`rounded-2xl border overflow-hidden ${panelClass}`}>
            <div className={`px-4 py-3.5 border-b flex items-center justify-between gap-3 ${isDark ? "border-slate-800 bg-gradient-to-r from-blue-950/50 to-slate-900" : "border-gray-100 bg-gradient-to-r from-blue-50 to-white"}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <Smartphone className={`w-4 h-4 shrink-0 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                <div>
                  <h3 className={`text-sm font-semibold ${textClass}`}>SMS API</h3>
                  <p className={`text-[11px] ${labelClass}`}>Giriş OTP mesajları</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Label htmlFor="sms-enabled" className={`text-xs ${labelClass}`}>Etkin</Label>
                <Switch
                  id="sms-enabled"
                  checked={form.sms_service_enabled}
                  onCheckedChange={(v) => setForm({ ...form, sms_service_enabled: !!v })}
                />
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div className={`rounded-xl border p-3 space-y-3 ${fieldWrapClass}`}>
                <div>
                  <Label className={`text-xs font-medium ${labelClass}`}>API URL</Label>
                  <Input
                    name="otp-sms-api-url"
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    value={form.sms_api_url}
                    onChange={(e) => setForm({ ...form, sms_api_url: e.target.value })}
                    className={`mt-1.5 h-9 text-sm ${inputClass}`}
                    placeholder="http://xmlapi.smspaneli.com/xmlSms-doping.php"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className={`text-xs font-medium ${labelClass}`}>Kullanıcı adı</Label>
                    <Input
                      name="otp-sms-api-user"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      value={form.sms_username}
                      onChange={(e) => setForm({ ...form, sms_username: e.target.value })}
                      className={`mt-1.5 h-9 text-sm ${inputClass}`}
                    />
                  </div>
                  <div>
                    <Label className={`text-xs font-medium ${labelClass}`}>
                      Şifre
                      {passwordSet && <span className={`ml-1.5 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>· kayıtlı</span>}
                    </Label>
                    <Input
                      type="password"
                      name="otp-sms-api-secret"
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      value={form.sms_password}
                      onChange={(e) => setForm({ ...form, sms_password: e.target.value })}
                      className={`mt-1.5 h-9 text-sm ${inputClass}`}
                      placeholder={passwordSet ? "Değiştirmek için girin" : "API şifresi"}
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div>
                    <Label className={`text-xs font-medium ${labelClass}`}>Originator (gönderici adı)</Label>
                    <p className={`text-[10px] mt-0.5 ${labelClass}`}>Varsayılanı seçin · max 32 karakter</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addOriginator} className={`h-8 text-xs ${isDark ? "border-slate-700" : ""}`}>
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    Ekle
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.sms_originators.map((originator, index) => {
                    const trimmed = originator.trim();
                    const isDefault = trimmed !== "" && form.sms_default_originator === trimmed;
                    return (
                      <div
                        key={`originator-${index}`}
                        className={`flex items-center gap-2 rounded-xl border p-2 ${isDefault
                          ? (isDark ? "border-blue-500/40 bg-blue-500/5" : "border-blue-200 bg-blue-50/50")
                          : fieldWrapClass
                        }`}
                      >
                        <Input
                          name={`otp-sms-originator-${index}`}
                          autoComplete="off"
                          data-lpignore="true"
                          data-1p-ignore="true"
                          maxLength={32}
                          value={originator}
                          onChange={(e) => updateOriginator(index, e.target.value)}
                          className={`h-9 text-sm flex-1 ${inputClass}`}
                          placeholder="ETELEKOM"
                        />
                        <Button
                          type="button"
                          variant={isDefault ? "default" : "outline"}
                          size="sm"
                          disabled={!trimmed}
                          onClick={() => setDefaultOriginator(trimmed)}
                          className={`h-9 text-xs shrink-0 ${isDefault ? "bg-blue-600 hover:bg-blue-700 text-white" : (isDark ? "border-slate-700" : "")}`}
                        >
                          {isDefault ? "Varsayılan" : "Seç"}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={form.sms_originators.length <= 1}
                          onClick={() => removeOriginator(index)}
                          className={`h-9 w-9 shrink-0 ${isDark ? "text-rose-400 hover:bg-rose-500/10" : "text-rose-600 hover:bg-rose-50"}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* WhatsApp panel */}
          <section className={`rounded-2xl border overflow-hidden ${panelClass}`}>
            <div className={`px-4 py-3.5 border-b flex items-center justify-between gap-3 ${isDark ? "border-slate-800 bg-gradient-to-r from-emerald-950/40 to-slate-900" : "border-gray-100 bg-gradient-to-r from-emerald-50 to-white"}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <MessageSquare className={`w-4 h-4 shrink-0 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                <div>
                  <h3 className={`text-sm font-semibold ${textClass}`}>WhatsApp API</h3>
                  <p className={`text-[11px] ${labelClass}`}>Giriş OTP mesajları</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Label htmlFor="wa-enabled" className={`text-xs ${labelClass}`}>Etkin</Label>
                <Switch
                  id="wa-enabled"
                  checked={form.whatsapp_service_enabled}
                  onCheckedChange={(v) => setForm({ ...form, whatsapp_service_enabled: !!v })}
                />
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div className={`rounded-xl border p-3 space-y-3 ${fieldWrapClass}`}>
                <div>
                  <Label className={`text-xs font-medium ${labelClass}`}>API URL</Label>
                  <Input
                    name="otp-wa-api-url"
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    value={form.whatsapp_api_url}
                    onChange={(e) => setForm({ ...form, whatsapp_api_url: e.target.value })}
                    className={`mt-1.5 h-9 text-sm ${inputClass}`}
                    placeholder="https://wa.etelekom.net/api/external/send"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className={`text-xs font-medium ${labelClass}`}>
                      API Token
                      {tokenSet && <span className={`ml-1.5 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>· kayıtlı</span>}
                    </Label>
                    <Input
                      type="password"
                      name="otp-wa-api-token"
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      value={form.whatsapp_api_token}
                      onChange={(e) => setForm({ ...form, whatsapp_api_token: e.target.value })}
                      className={`mt-1.5 h-9 text-sm font-mono ${inputClass}`}
                      placeholder={tokenSet ? "Değiştirmek için girin" : "Bearer token"}
                    />
                  </div>
                  <div>
                    <Label className={`text-xs font-medium ${labelClass}`}>Slot</Label>
                    <Input
                      type="number"
                      min={1}
                      name="otp-wa-slot"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      value={form.whatsapp_slot}
                      onChange={(e) => setForm({ ...form, whatsapp_slot: parseInt(e.target.value, 10) || 1 })}
                      className={`mt-1.5 h-9 text-sm ${inputClass}`}
                    />
                    <p className={`text-[10px] mt-1 ${labelClass}`}>WhatsApp hat numarası / kanal slotu</p>
                  </div>
                </div>
              </div>

              <div className={`rounded-xl border px-3 py-3 text-xs leading-relaxed ${isDark ? "border-slate-800 bg-slate-950/30 text-slate-400" : "border-gray-100 bg-gray-50 text-gray-600"}`}>
                <p className="font-medium mb-1.5 flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-emerald-500" />
                  WhatsApp &quot;Eksik&quot; görünüyorsa
                </p>
                <ul className="list-disc list-inside space-y-0.5 opacity-90">
                  <li>API URL ve token birlikte doldurulmalı</li>
                  <li>Servis etkin switch&apos;i açık olmalı</li>
                  <li>SSL sertifikası sunucuda doğrulanabilir olmalı</li>
                </ul>
              </div>
            </div>
          </section>

          <section className={`rounded-2xl border overflow-hidden ${panelClass}`}>
            <div className={`px-4 py-3.5 border-b flex items-center justify-between gap-3 ${isDark ? "border-slate-800 bg-gradient-to-r from-cyan-950/40 to-slate-900" : "border-gray-100 bg-gradient-to-r from-cyan-50 to-white"}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <Shield className={`w-4 h-4 shrink-0 ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                <div>
                  <h3 className={`text-sm font-semibold ${textClass}`}>Hızlı Giriş Linki</h3>
                  <p className={`text-[11px] ${labelClass}`}>SMS/WhatsApp mobil onay bağlantısı</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Label htmlFor="link-enabled" className={`text-xs ${labelClass}`}>Etkin</Label>
                <Switch
                  id="link-enabled"
                  checked={form.login_link_enabled}
                  onCheckedChange={(v) => setForm({ ...form, login_link_enabled: !!v })}
                />
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div className={`rounded-xl border p-3 space-y-3 ${fieldWrapClass}`}>
                <div>
                  <Label className={`text-xs font-medium ${labelClass}`}>Link taban URL</Label>
                  <Input
                    value={form.login_link_base_url}
                    onChange={(e) => setForm({ ...form, login_link_base_url: e.target.value })}
                    className={`mt-1.5 h-9 text-sm font-mono ${inputClass}`}
                    placeholder="https://cc2.etelekom.net/otp"
                  />
                  <p className={`text-[10px] mt-1 ${labelClass}`}>Mesajdaki link: taban URL + / + token (ör. …/otp/a1B2c3D4e5F6g7H8)</p>
                </div>
                <div>
                  <Label className={`text-xs font-medium ${labelClass}`}>
                    Link imza anahtarı (opsiyonel)
                    {linkSecretSet && <span className={`ml-1.5 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>· kayıtlı</span>}
                  </Label>
                  <Input
                    type="password"
                    value={form.login_link_secret}
                    onChange={(e) => setForm({ ...form, login_link_secret: e.target.value })}
                    className={`mt-1.5 h-9 text-sm font-mono ${inputClass}`}
                    placeholder={linkSecretSet ? "Değiştirmek için girin" : "Boş bırakılırsa JWT secret kullanılır"}
                  />
                </div>
              </div>
              <div className={`rounded-xl border px-3 py-3 text-xs leading-relaxed ${isDark ? "border-slate-800 bg-slate-950/30 text-slate-400" : "border-gray-100 bg-gray-50 text-gray-600"}`}>
                Kullanıcı bazında admin panelinden açılmalıdır. Link tek kullanımlık, OTP süresi kadar geçerlidir (~5 dk).
              </div>
            </div>
          </section>
        </div>

        <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border px-4 py-3 ${isDark ? "border-slate-800 bg-slate-900/40" : "border-gray-200 bg-gray-50/80"}`}>
          <p className={`text-xs ${labelClass}`}>Değişiklikler kaydedilene kadar giriş OTP akışına yansımaz.</p>
          <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white sm:min-w-[140px]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Ayarları Kaydet
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OtpApiSettings;
